import { TicketInfo } from 'src/models/InvoiceModel';
import { ProductModel } from 'src/models/ProductModel';
import { FORM_ID } from 'src/models/FormModel';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import IdentifierSelector from 'src/components/Control/IdentifierSelector';
import Row from 'src/components/UI/Row';
import Col2 from 'src/components/UI/Col2';
import Subtitle from 'src/components/UI/Subtitle';

interface Props {
  form: FORM_ID;
  product: ProductModel;
  tickets: TicketInfo[];
  onChange: (tickets: TicketInfo[]) => void;
  eventId?: number;
  disabled?: boolean;
  namePrefix?: string;
}

const TicketInformationFields = (props: Props) => {
  const {
    form, product, tickets, onChange, eventId, disabled, namePrefix = 'tickets',
  } = props;

  const hasAnyField = !!product.first_name_required
    || !!product.last_name_required
    || !!product.email_required
    || !!product.phone_required
    || !!product.identifier_required;

  if (!hasAnyField || tickets.length === 0) {
    return null;
  }

  const handleFieldChange = (index: number, field: string, value: string) => {
    const updated = tickets.map((ticket, i) => {
      if (i !== index) return ticket;
      return { ...ticket, [field]: value };
    });
    onChange(updated);
  };

  const hasNameFields = !!product.first_name_required || !!product.last_name_required;
  const hasContactFields = !!product.email_required || !!product.phone_required;

  return (
    <>
      <Subtitle>Ticket Information</Subtitle>
      {tickets.map((ticket, index) => (
        <div key={`ticket-${index}`} style={{ marginBottom: '16px' }}>
          {tickets.length > 1 && (
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              Ticket #{index + 1}
            </div>
          )}
          {hasNameFields && (
            <Row height="auto">
              {!!product.first_name_required && (
                <Col2>
                  <FormTextField
                    form={form}
                    label="First Name"
                    name={`${namePrefix}[${index}].first_name`}
                    onChange={(val) => handleFieldChange(index, 'first_name', val)}
                    value={ticket.first_name || ''}
                    disabled={disabled} />
                </Col2>
              )}
              {!!product.last_name_required && (
                <Col2>
                  <FormTextField
                    form={form}
                    label="Last Name"
                    name={`${namePrefix}[${index}].last_name`}
                    onChange={(val) => handleFieldChange(index, 'last_name', val)}
                    value={ticket.last_name || ''}
                    disabled={disabled} />
                </Col2>
              )}
            </Row>
          )}
          {hasContactFields && (
            <Row height="auto">
              {!!product.email_required && (
                <Col2>
                  <FormTextField
                    form={form}
                    label="Email"
                    name={`${namePrefix}[${index}].email`}
                    onChange={(val) => handleFieldChange(index, 'email', val)}
                    value={ticket.email || ''}
                    disabled={disabled} />
                </Col2>
              )}
              {!!product.phone_required && (
                <Col2>
                  <FormTextField
                    form={form}
                    label="Phone"
                    name={`${namePrefix}[${index}].phone`}
                    onChange={(val) => handleFieldChange(index, 'phone', val)}
                    value={ticket.phone || ''}
                    disabled={disabled} />
                </Col2>
              )}
            </Row>
          )}
          {!!product.identifier_required && (
            <Row height="auto">
              <Col2>
                <IdentifierSelector
                  identifiersString={product.identifiers || ''}
                  eventId={eventId}
                  productId={product.id}
                  value={ticket.identifiers}
                  onChange={(val) => handleFieldChange(index, 'identifiers', val)}
                  form={form}
                  name={`${namePrefix}[${index}].identifiers`}
                  disabled={disabled} />
              </Col2>
            </Row>
          )}
        </div>
      ))}
    </>
  );
};

export default TicketInformationFields;
